import React, { useState, useEffect, FormEvent } from 'react';
import {
    Drawer,
    Button,
    TextInput,
    Select,
    NumberInput,
    Textarea,
    Notification,
    Group,
    Title,
    Box,
    Divider,
    Text
} from '@mantine/core';
import axios from 'axios';
import { getToken } from '../../../auth/service/authService';
import { DateInput, DateValue } from '@mantine/dates';
import { BackendUrl } from '../../../util/config';

// Our contract type enum
const contractTypes = [
    { value: 'MONTHLY', label: 'Monatlich' },
    { value: 'YEARLY', label: 'Jährlich' }
];

// Basic shape for a storage location
interface StorageLocation {
    id: number;
    name: string;
}

interface CreateRentContractProps {
    onCreatedRentContract: () => void;
    organisationId: string;
}

export const CreateRentContract: React.FC<CreateRentContractProps> = ({
                                                                          onCreatedRentContract,
                                                                          organisationId
                                                                      }) => {
    const [opened, setOpened] = useState(false);

    // Form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [description, setDescription] = useState('');
    const [type, setType] = useState('MONTHLY');
    const [monthlyPrice, setMonthlyPrice] = useState<number>(0);

    // For location selection
    const [availableLocations, setAvailableLocations] = useState<StorageLocation[]>([]);
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

    // For notifications
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Automatically check availability when both dates are selected or changed
    useEffect(() => {
        if (startDate && endDate) {
            handleCheckAvailability();
        }
        // Only re-run when dates change
    }, [startDate, endDate]);

    /**
     * 1) Called when user picks new date range or automatically via useEffect above
     */
    const handleCheckAvailability = async () => {
        if (!startDate || !endDate) {
            setErrorMessage('Please select both start and end dates first.');
            return;
        }

        try {
            setLoading(true);
            setErrorMessage(null);
            setSuccessMessage(null);

            const token = getToken();
            const startStr = startDate.toISOString().split('T')[0];
            const endStr = endDate.toISOString().split('T')[0];

            const response = await axios.get<StorageLocation[]>(
                `${BackendUrl}/storage-locations/available?start=${startStr}&end=${endStr}&orgId=${organisationId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token ? `Bearer ${token}` : ''
                    }
                }
            );
            setAvailableLocations(response.data);
            setSelectedLocationId(null);
        } catch (err: any) {
            console.error(err);
            setErrorMessage(err?.response?.data || 'Error fetching available locations');
        } finally {
            setLoading(false);
        }
    };

    /**
     * 2) Handle final form submission to create the contract
     */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setSuccessMessage(null);
        setErrorMessage(null);

        if (!startDate || !endDate) {
            setErrorMessage('Bitte wähle einen Zeitraum aus');
            return;
        }
        if (!selectedLocationId) {
            setErrorMessage('Bitte wähle einen Stellplatz aus der Liste aus');
            return;
        }

        const payload = {
            firstName,
            lastName,
            email,
            phoneNumber,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            description,
            type,
            monthlyPrice
        };

        try {
            setLoading(true);
            const token = getToken();
            await axios.post(
                `${BackendUrl}/rent-contracts?storageLocationId=${selectedLocationId}`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token ? `Bearer ${token}` : ''
                    }
                }
            );

            setSuccessMessage('Rent contract created successfully!');
            setFirstName('');
            setLastName('');
            setEmail('');
            setPhoneNumber('');
            setStartDate(null);
            setEndDate(null);
            setDescription('');
            setType('MONTHLY');
            setMonthlyPrice(0);
            setAvailableLocations([]);
            setSelectedLocationId(null);

            onCreatedRentContract();
        } catch (error: any) {
            console.error(error);
            setErrorMessage(error?.response?.data || 'Error creating rent contract');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button variant="filled" onClick={() => setOpened(true)}>
                Mietvertrag erstellen
            </Button>

            <Drawer opened={opened} onClose={() => setOpened(false)} title="Erstelle Mietvertrag" padding="md" size="lg">
                <Box>
                    {successMessage && (
                        <Notification color="teal" mb="sm" onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Notification>
                    )}
                    {errorMessage && (
                        <Notification color="red" mb="sm" onClose={() => setErrorMessage(null)}>
                            {errorMessage}
                        </Notification>
                    )}
                </Box>

                <form onSubmit={handleSubmit}>
                    <Title order={4} mb="md">
                        Mietvertrag Details
                    </Title>
                    <DateInput
                        label="Start Datum"
                        placeholder="Wähle Startdatum"
                        required
                        value={startDate}
                        onChange={(date: DateValue) => setStartDate(date)}
                        mb="sm"
                    />

                    <DateInput
                        label="End Datum"
                        placeholder="Wähle Enddatum"
                        required
                        value={endDate}
                        onChange={(date: DateValue) => setEndDate(date)}
                        mb="sm"
                    />

                    <Select
                        label="Verfügbare Stellplätze"
                        placeholder="Wähle Stellplatz aus"
                        data={availableLocations.map(loc => ({ value: loc.id.toString(), label: loc.name }))}
                        value={selectedLocationId}
                        onChange={val => setSelectedLocationId(val!)}
                        disabled={availableLocations.length === 0}
                        mb="sm"
                    />
                    <Select label="Vertragstyp" data={contractTypes} value={type} onChange={val => setType(val!)} required mb="sm" />
                    <Text>*Diese Daten sind nach Erstellung nicht mehr veränderbar</Text>

                    <Divider mt={"15px"}></Divider>

                    <NumberInput label="Monatlicher Preis" placeholder="100" required value={monthlyPrice} onChange={val => setMonthlyPrice(val as number || 0)} mb="sm" />
                    <TextInput label="Vorname" placeholder="Max" required value={firstName} onChange={e => setFirstName(e.currentTarget.value)} mb="sm" />
                    <TextInput label="Nachname" placeholder="Mustermann" required value={lastName} onChange={e => setLastName(e.currentTarget.value)} mb="sm" />
                    <TextInput label="Email" placeholder="max.mustermann@example.com" required value={email} onChange={e => setEmail(e.currentTarget.value)} mb="sm" />
                    <TextInput label="Telefonnummer" placeholder="0213 247924792" required value={phoneNumber} onChange={e => setPhoneNumber(e.currentTarget.value)} mb="sm" />
                    <Textarea label="Beschreibung" placeholder="Zusätzliche Informationen" value={description} onChange={e => setDescription(e.currentTarget.value)} mb="sm" />



                    <Text>*Diese Daten sind nach Erstellung veränderbar</Text>
                    <Group p="right" mt="md">
                        <Button variant={"filled"} type="submit" loading={loading}>
                            Erstelle Vertrag
                        </Button>
                    </Group>
                </form>
            </Drawer>
        </>
    );
};
